import React, {
  useState,
  useCallback,
  useEffect,
  useContext,
  useMemo,
} from 'react';
import Dialog from '../../../../BaseComponents/Dialog/EDDialog';
import {
  RutinUrvalType,
  TEDUrvalVisibility,
  IUIUrval,
  NodeUrvalType,
  IUIPage,
  TEDUrvalType,
} from '../../../../Types/UrvalTypes';
import EDIcon from '../../../../BaseComponents/Icon/EDIcon';
import UrvalSida from '../Sida/UrvalSida';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import DefaultUrval from '../DefaultUrval/DefaultUrval';
import UrvalActions from '../UrvalActions/UrvalActions';
import {
  convertUrval,
  convertUrvalLogError,
} from '../SavedUrval/Utils/convertSavedUrval';
import { ApolloError, useApolloClient } from '@apollo/client';
import UrvalHitIndicator, {
  IDetaljurvalInfo,
} from '../HitIndicator/UrvalHitIndicator';
import {
  ModelContextConsumer,
  AvenyCollectionMap,
} from '../../../../Model/ModelContext/ModelContext';
import { UrvalFieldSortModeType } from '../UrvalFieldPicker/Types/UrvalFieldPickerTypes';
import { useToggle } from '../../../../Hooks/booleanHooks';
import {
  RutinUrvalListQueryVariables,
  RutinUrvalListQuery_routineselections,
} from '../SavedUrval/Queries/RutinUrvalListQuery/_QueryTypes/RutinUrvalListQuery';
import { SaveNewUrvalMutationUpdaterType } from '../Toolbar/Components/SaveNewUrval/Mutations/SaveNewUrvalMutation';
import RemoveUrvalMutation, {
  RemoveUrvalMutationUpdaterType,
} from '../SavedUrval/Mutations/RemoveUrvalMutation';
import { IDialogFooterActionButton } from '../../../../BaseComponents/Dialog/DialogFooter/EDDialogFooter';
import { UrvalContextProvider } from '../../Context/UrvalContext';
import { TEDSelectionVisibility } from '../../../../globalQueryTypes';
import { useCommit } from '../../../../Hooks/useCommit';
import EDTabs from '../../../../BaseComponents/Tabs/EDTabs';
import EDTab from '../../../../BaseComponents/Tabs/Tab/EDTab';
import EDIconButton from '../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import EDTooltip from '../../../../BaseComponents/Tooltip/EDTooltip';
import { getUrvalPageHeights } from '../UrvalFieldPicker/Utils/UrvalConditionUtils';
import { useIsExtraSmallScreen } from '../../../../Hooks/useFullscreenView';
import { getEmptyUrval } from '../../Utils/urvalUtils';
import { getVisibilityLabel } from '../SavedUrval/Utils/SavedUrvalUtils';
import UrvalToolbarTop from '../Toolbar/UrvalToolbarTop';
import { EDAutoCompleteEdit } from '../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import { queryNodeUrval } from '../SavedUrval/Queries/NodeUrvalQuery/queryNodeUrval';
import Logger from '../../../../Utils/Logger';
import mergeSort from '../../../../Utils/mergeSort';
import { tryConvertUIUrvalToTEDUrval } from '../../Utils/convertUIUrvalToTEDUrval';
import EDAvenyProgress from '../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import EDKeybind from '../../../../BaseComponents/Keybinds/EDKeybind';
import EDConfirmationDialog from '../../../../BaseComponents/ConfirmationDialog/EDConfirmationDialog';
import UrvalFieldPickerDialog from '../UrvalFieldPicker/Components/UrvalFieldPickerDialog';
import { useSetFavoritUrval } from '../Toolbar/Components/FavoritUrvalButton/Mutations/SetFavoritUrval/SetFavoritUrvalMutation';
import { useClearFavoritUrval } from '../Toolbar/Components/FavoritUrvalButton/Mutations/ClearFavoritUrval/ClearFavoritUrvalMutation';
import { findCollectionResult } from '../../../../Model/avenyModelUtils';
import { useIsBreakpoint } from '../../../BreakpointContext/BreakpointContextUtils';
import { NodeUrvalQuery_node_TEDSelection_order } from '../SavedUrval/Queries/NodeUrvalQuery/_QueryTypes/NodeUrvalQuery';
import EDTypography from '../../../../BaseComponents/Typography/EDTypography';
import { RutinSettingsContext } from '../../../../Features/Delsystem/_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import { rutiner } from '../../../../Features/Delsystem/_Common/Const/Rutiner';

interface IProps {
  setUrval: (newUrval: IUIUrval) => void;
  collectionName: string;
  rutinUrvalList?: RutinUrvalType[] | null;
  rutinUrvalVariables?: RutinUrvalListQueryVariables;
  routineID?: number;
  usageCode?: string;
  codeSystemID?: string;
  urval: IUIUrval | null;
  open: boolean;
  onClose: () => void;
  onCommit?: () => void;
  propagateSetUrval?: boolean;
  kodbehorighet?: React.ReactNode;
  openFieldPickerWithAllExpandableOpen?: boolean;
  fieldSortMode?: UrvalFieldSortModeType;
  kodplanUrval: boolean;
  /** Vilka synlighetsval som ska gå att välja på när man sparar ett urval, denna lista kommer att filtreras beroende på behörigheter, Default: [privat, gemensamt] */
  saveUrvalAvailableVisibilityOptions?: TEDUrvalVisibility[];
  saveNewUrvalMutationUpdater?: SaveNewUrvalMutationUpdaterType;
  removeUrvalMutationUpdater?: RemoveUrvalMutationUpdaterType;
  forceSave?: boolean;
  detaljurvalInfo?: IDetaljurvalInfo;
  placeHolder?: string;
  refetchCollectionQuery?: (variables: {
    userSelection: TEDUrvalType | undefined;
  }) => void;
  overrideUrvalSidaComp?: (
    page: IUIPage,
    setPage: (newPage: IUIPage) => void
  ) => React.ReactNode;
  leftFooterActions?: IDialogFooterActionButton[];
  disableVirtualTreeSelect?: boolean;
  hideUrvalHitIndicator?: boolean;
  disableActionButton?: boolean;
  setSortInfoFromSelectionOrder?: (
    selectionOrder: NodeUrvalQuery_node_TEDSelection_order[]
  ) => void;
}

interface IInnerProps extends IProps {
  loading: boolean;
  error?: ApolloError | Error;
  pickedSavedUrval?: NodeUrvalType;
  cancel: () => void;
  commit: () => void;
  setPage: (pageIndex: number) => (newPage: IUIPage) => void;
  addNewPage: () => void;
  removePage: (pageNr: number) => void;
  urval: IUIUrval | null;
  avenyCollectionMap: AvenyCollectionMap;
  height: number;
  isSmallScreen: boolean;
  setSelectionOrder: (
    selectionOrder: NodeUrvalQuery_node_TEDSelection_order[]
  ) => void;
}

type CategoriesedSelection = RutinUrvalListQuery_routineselections & {
  category: string;
  id: string;
  namn: string;
};

const useStyles = makeStyles<any>()((theme: Theme, props: IInnerProps) => {
  const tabLength = props.isSmallScreen ? 72 : 160;
  const scrollOffset = props.isSmallScreen ? 40 : 0;
  return {
    /** Styles applied to the bottom area of the dialog header  */
    dialogHeaderBottomArea: {
      display: 'flex',
      flexDirection: 'column',
    },
    kodbehorighetSpacing: {
      left: theme.spacing(1),
      [theme.breakpoints.up('sm')]: {
        marginRight: theme.spacing(6),
      },
    },
    newPageButtonContainer: {
      position: 'absolute',
      left: `min(${
        props.urval ? scrollOffset + props.urval.pages.length * tabLength : 0
      }px, calc(100% - 124px))`,
    },
    dialogPaper: {
      [theme.breakpoints.up('md')]: {
        width: 'calc(100% - 16px)',
        height: 'calc(100% - 16px)',
        minWidth: '200px',
      },
      [theme.breakpoints.up('lg')]: {
        minWidth: '1000px',
        width: 1000,
        height: 'initial',
      },
    },
    dialogContent: {
      height: `${props.height}px`,
    },
    pageDivider: { margin: theme.spacing(0, -3) },
    /** Styles applied to the dialog subtitle if a new urval is created */
    subtitleNewUrval: { fontStyle: 'italic' },
    headerRoot: {
      padding: 0,
    },
    headerTopArea: {
      padding: theme.spacing(3, 0, 0, 3),
      [theme.breakpoints.down('md')]: {
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(2, 0, 0, 3),
      },
    },
    removeButton: {
      position: 'absolute',
      right: '2px',
      top: '2px',
    },
    tabsContainer: {
      display: 'flex',
      width: '100%',
    },
    tabsEmptyContainer: {
      justifyContent: 'flex-end',
    },
    tab: {
      height: 52,
    },
    tabs: {
      width: '100%',
      paddingRight: 64,
    },
    menuButton: {
      top: theme.spacing(4.2),
      right: theme.spacing(0),
      position: 'absolute',
    },
    selection: {
      '&:hover': {
        cursor: 'pointer',
        textDecoration: 'none',
      },
    },
  } as const;
});

export const standardGrundurvalDisabledReason =
  'Standardgrundurval går ej att ändra';

export const UrvalDialog: React.FunctionComponent<
  React.PropsWithChildren<IInnerProps>
> = props => {
  const { cancel, addNewPage: addNewPageProp } = props;
  const rutinSettingsContext = useContext(RutinSettingsContext);
  const apolloClient = useApolloClient();

  const isSmall = useIsExtraSmallScreen();
  const { classes, cx } = useStyles(props, {
    props: props as any,
  });
  const [tab, setTab] = useState<number | undefined>(0);
  const [openField, toggleOpenField] = useToggle();
  const [saveNewUrvalDialogOpen, setSaveNewUrvalDialogOpen] = useState(false);
  const [deleteConfirmationDetails, setDeleteConfirmationDetails] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const onExited = useCallback(() => {
    setTab(0);
    cancel();
  }, [cancel]);

  const addTab = useCallback(() => {
    addNewPageProp();
    setTab(props.urval?.pages.length ?? 0);
  }, [addNewPageProp, props.urval?.pages.length]);

  const renderHeaderTopArea = (
    addNewPage: () => void,
    kodbehorighet: React.ReactNode | undefined
  ) => {
    return (
      <>
        <div className={classes.kodbehorighetSpacing}>{kodbehorighet}</div>
        <div className={classes.menuButton}>
          <UrvalToolbarTop
            setUrval={setUrval}
            setTab={setTab}
            collectionName={props.collectionName}
            rutinUrvalList={props.rutinUrvalList}
            rutinUrvalVariables={props.rutinUrvalVariables}
            addNewPage={addNewPage}
            pickedSavedUrval={pickedSavedUrval}
            routineID={props.routineID}
            kodplanUrval={props.kodplanUrval}
            codeSystemID={props.codeSystemID}
            urval={urval || getEmptyUrval()}
            openFieldPickerWithAllExpandableOpen={
              props.openFieldPickerWithAllExpandableOpen
            }
            fieldSortMode={props.fieldSortMode}
            saveUrvalAvailableVisibilityOptions={
              props.saveUrvalAvailableVisibilityOptions
            }
            saveNewUrvalMutationUpdater={props.saveNewUrvalMutationUpdater}
            removeUrvalMutationUpdater={props.removeUrvalMutationUpdater}
            saveNewUrvalDialogOpen={saveNewUrvalDialogOpen}
            setSaveNewUrvalDialogOpen={setSaveNewUrvalDialogOpen}
            addTab={addTab}
            fieldPickerDialogOpen={openField}
            toggleFieldPickerDialogOpen={toggleOpenField}
          />
        </div>
      </>
    );
  };

  const renderHeaderBottomArea = (
    _addNewPage: () => void,
    kodbehorighet: React.ReactNode | undefined,
    disabled?: boolean,
    disabledReason?: string
  ) => {
    const showTabs = props.urval && props.urval.pages.length > 1;
    return (
      <>
        <div className={classes.kodbehorighetSpacing}>{kodbehorighet}</div>
        <div
          className={cx(classes.tabsContainer, {
            [classes.tabsEmptyContainer]: !showTabs,
          })}
        >
          {showTabs && (
            <>
              <EDTabs
                value={tab ?? false}
                onChange={setTab}
                indicatorColor="white"
                className={classes.tabs}
                scrollButtons={isSmall ? true : 'auto'}
                noBoxShadow
              >
                {props.urval &&
                  props.urval.pages.map((x, index) => (
                    <EDTab
                      key={x.id}
                      classes={{ root: classes.tab }}
                      label={
                        <>
                          {`Sida ${index + 1}`}
                          <EDIconButton
                            onClick={(event: React.MouseEvent) => {
                              event.stopPropagation();
                              setTab(
                                tab === undefined || tab < index ? tab : tab - 1
                              );
                              props.removePage(index + 1);
                            }}
                            className={classes.removeButton}
                            iconSize="small"
                            color="inherit"
                            icon="close"
                            tooltip="Ta bort sida"
                            margin="none"
                            padding="none"
                          />
                        </>
                      }
                    />
                  ))}
              </EDTabs>
              <EDTooltip title="Skapa ny sida">
                <span className={classes.newPageButtonContainer}>
                  <EDTab
                    disabled={disabled}
                    icon={<EDIcon iconType="add" color="inherit" />}
                    classes={{ root: classes.tab }}
                    onClick={addTab}
                  />
                </span>
              </EDTooltip>
            </>
          )}
        </div>
      </>
    );
  };
  const onUseClick = () => {
    if (props.refetchCollectionQuery) {
      const userSelection = tryConvertUIUrvalToTEDUrval(props.urval);
      props.refetchCollectionQuery({ userSelection });
    }
    props.commit();
  };

  const getFooterActions = (): IDialogFooterActionButton[] => {
    const needSaveBeforeUse =
      props.forceSave &&
      props.urval &&
      (props.urval.savedUrvalProps == null ||
        !props.urval.savedUrvalProps.urvalID);

    return [
      {
        onClick: onClose,
        label: 'Avbryt',
      },
      {
        onClick: onUseClick,
        buttonProps: {
          disabled:
            Boolean(needSaveBeforeUse) || loading || props.disableActionButton,
          disabledReason: needSaveBeforeUse
            ? 'Urvalet måste sparas innan det går att använda'
            : props.disableActionButton
            ? 'Det måste finnas resurser valda'
            : undefined,
          tooltip: 'Använd ( Enter )',
        },
        label: 'Använd',
      },
    ];
  };

  const {
    setUrval,
    onClose,
    kodbehorighet,
    collectionName,
    routineID,
    open,
    addNewPage,
    pickedSavedUrval,
    urval,
    loading,
    error,
    setPage,
    removePage,
    avenyCollectionMap,
    placeHolder,
  } = props;

  useEffect(() => {
    if (pickedSavedUrval && !urval) {
      setUrval(
        convertUrvalLogError(
          pickedSavedUrval,
          props.collectionName,
          avenyCollectionMap,
          `fel vid inläsning av sparat urval för ${props.collectionName}`
        )
      );
    }
  });

  const setFavoritUrval = useSetFavoritUrval();
  const clearFavoritUrval = useClearFavoritUrval();

  let options: CategoriesedSelection[] = [];

  if (props.rutinUrvalList && props.rutinUrvalList !== undefined) {
    options = props.rutinUrvalList.map(option => {
      let parsedCategory = getVisibilityLabel(option.synlighet).PO;
      return {
        category: parsedCategory,
        ...option,
      };
    });
  }
  const sortBy = [
    'Mina urval',
    'Gemensamma urval',
    'Standardurval',
    'Gruppurval',
    'Standard grundurval',
    'Övriga sparade urval',
  ];

  const customSort = (
    data: CategoriesedSelection[],
    sortBy: string[],
    sortField: keyof CategoriesedSelection
  ) => {
    const sortByObject = sortBy.reduce(
      (obj: Record<string, number>, item: string, index: number) => {
        return {
          ...obj,
          [item]: index,
        };
      },
      {}
    );
    return mergeSort<CategoriesedSelection>(
      data,
      (a: CategoriesedSelection, b: CategoriesedSelection) =>
        sortByObject[a[sortField] as string] - sortByObject[b[sortField] as string]
    );
  };
  const sortedOptions = customSort(options, [...sortBy], 'category');

  const onChange = (options: CategoriesedSelection) => {
    let id: string = options?.id || '';
    queryNodeUrval(apolloClient, id)
      .then(options => {
        props.setUrval(
          convertUrval(options, props.collectionName, props.avenyCollectionMap)
        );
        if (options.order && options.order.length > 0) {
          props.setSelectionOrder(options.order);
        }
      })
      .catch(error => {
        Logger.error(error);
      });
  };
  const initialUrval =
    rutiner[rutinSettingsContext.activeRoutine?.routine.rutinKod || '']
      ?.initialUrval;
  return (
    <Dialog
      open={open}
      title={'Urval'}
      subtitle={
        <RemoveUrvalMutation
          rutinUrvalListVariables={props.rutinUrvalVariables}
          removeUrvalMutationUpdater={props.removeUrvalMutationUpdater}
        >
          {removeUrval => (
            <>
              <EDAutoCompleteEdit<CategoriesedSelection>
                label={'Valt Urval'}
                notSelectedLabel={'Spara för att namnge urvalet'}
                width={280}
                value={
                  sortedOptions.find(
                    option => option.namn === pickedSavedUrval?.namn
                  ) ?? null
                }
                groupBy={options => options.category}
                options={sortedOptions}
                getOptionLabel={options => options.namn}
                onChange={option => {
                  if (option) {
                    onChange(option);
                  }
                }}
                onReselectCurrentValue={option => {
                  if (option) {
                    onChange(option);
                  }
                }}
                isHeadlineSelection
                placeholder={placeHolder}
                inputDarkTheme
                actions={[
                  {
                    id: 'Favourite',
                    tooltip: option => {
                      if (initialUrval) {
                        return 'Starturval';
                      }

                      if (option.anvFavorit) {
                        return 'Avmarkera starturval';
                      } else {
                        return 'Markera som starturval';
                      }
                    },
                    icon: option => {
                      const isInitialUrval = initialUrval === option.id;
                      if (isInitialUrval || option.anvFavorit) {
                        return 'star';
                      }
                      return 'star_border';
                    },
                    hide: option => {
                      const isInitialUrval = initialUrval === option.id;
                      const hasInitialUrval =
                        initialUrval !== undefined && initialUrval !== null;

                      return (
                        Boolean(routineID) && hasInitialUrval && !isInitialUrval
                      );
                    },
                    onClick: option => {
                      if (routineID) {
                        if (option.anvFavorit) {
                          clearFavoritUrval(option.id, routineID);
                        } else {
                          setFavoritUrval(option.id, routineID);
                        }
                      }
                    },
                    disabled: option => {
                      const isInitialUrval = initialUrval === option.id;
                      return isInitialUrval;
                    },
                  },
                  {
                    id: 'delete',
                    icon: 'delete',
                    onClick: option => {
                      setDeleteConfirmationDetails({
                        id: option.id,
                        name: option.namn,
                      });
                    },
                    tooltip: 'Ta bort',
                    hide: option =>
                      option.synlighet === 'dbstandard' ||
                      option.synlighet === 'standard',
                  },
                ]}
                testId="AutoCompleteEdit-PickedUrval"
              />
              <EDConfirmationDialog
                title="Ta bort Urval"
                confirmCaption="Ta bort"
                open={Boolean(deleteConfirmationDetails)}
                onClose={() => setDeleteConfirmationDetails(null)}
                onConfirm={() => {
                  if (deleteConfirmationDetails) {
                    removeUrval(deleteConfirmationDetails.id);
                  }
                  setDeleteConfirmationDetails(null);
                }}
              >
                {' '}
                <>
                  Du har markerat att du vill ta bort Urvalet{' '}
                  <EDTypography component="span" bold>
                    {deleteConfirmationDetails?.name || `namn saknas`}
                  </EDTypography>
                  <br />
                  Vill du verkligen ta bort det?
                </>
              </EDConfirmationDialog>
            </>
          )}
        </RemoveUrvalMutation>
      }
      onClose={onClose}
      headerBottomArea={renderHeaderBottomArea(addNewPage, undefined)}
      topToolbarArea={renderHeaderTopArea(addNewPage, kodbehorighet)}
      headerProps={{
        classes: {
          content: '', // FIXME: borde inte behöva skriva in alla klasser (se annan FIXME i UrvalFieldPickerDialog)
          root: classes.headerRoot,
          subtitle: '',
          title: '',
          tools: '',
          topArea: classes.headerTopArea,
          cursor: '',
          bottomArea: classes.dialogHeaderBottomArea,
          topToolbar: '',
          titleContainer: '',
        },
        subtitleTypographyProps: {
          className: cx(classes.selection, {
            [classes.subtitleNewUrval]: !pickedSavedUrval,
          }),
        },
      }}
      maxWidth={false}
      footerActions={getFooterActions()}
      footerResetResourceActions={props.leftFooterActions}
      footerContent={
        open && urval && !props.hideUrvalHitIndicator ? (
          <UrvalHitIndicator
            collection={findCollectionResult(
              props.avenyCollectionMap,
              collectionName
            ).unsafelyUnwrap()}
            urval={urval}
            detaljurvalInfo={props.detaljurvalInfo}
            routineID={routineID}
          />
        ) : null
      }
      classes={{ paper: classes.dialogPaper, content: classes.dialogContent }}
      onExited={onExited}
    >
      <EDKeybind
        keymap="Enter"
        onKeyDown={e => {
          e.preventDefault();
          // Defer to next tick so React can commit any batched state updates
          // triggered by input flush-on-Enter (e.g. ConditionValueEdit debounce).
          setTimeout(onUseClick, 0);
        }}
        global
      >
        {loading && <EDAvenyProgress />}
        {!loading && urval && error && error.message}
        {!loading &&
          urval &&
          tab !== undefined &&
          urval.pages[tab] &&
          !props.overrideUrvalSidaComp && (
            <UrvalSida
              indention={0}
              pageNr={tab + 1}
              page={urval.pages[tab]}
              setPage={setPage(tab)}
              collectionName={collectionName}
              removePage={removePage}
              toggleFieldDialog={toggleOpenField}
            />
          )}
        {!loading &&
          urval &&
          tab !== undefined &&
          props.overrideUrvalSidaComp &&
          props.overrideUrvalSidaComp(urval.pages[tab], setPage(tab))}
      </EDKeybind>
      {!loading && urval && tab !== undefined && urval.pages[tab] && (
        <UrvalFieldPickerDialog
          open={openField}
          onClose={toggleOpenField}
          page={urval.pages[tab]}
          setPage={setPage(tab)}
          collectionName={collectionName}
          openWithAllExpandableOpen={props.openFieldPickerWithAllExpandableOpen}
          fieldSortMode={props.fieldSortMode}
          disableVirtualTreeSelect={props.disableVirtualTreeSelect}
        />
      )}
    </Dialog>
  );
};

/**
 * En dialog komponent som visar UrvalPages för ett givet urval samt tillåter sparande och laddande av urval.
 */
const EnhancedUrvalDialog: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { onCommit, setSortInfoFromSelectionOrder } = props;
  const [selectionOrder, setSelectionOrder] =
    useState<NodeUrvalQuery_node_TEDSelection_order[]>();

  const handleCommit = useCallback(() => {
    if (selectionOrder && setSortInfoFromSelectionOrder) {
      setSortInfoFromSelectionOrder(selectionOrder);
    }
    onCommit?.();
  }, [onCommit, setSortInfoFromSelectionOrder, selectionOrder]);

  // Urvalet kan vara null när dialogen öppnas,
  // men inte sättas till null igen efter det.
  const setUrvalProp = props.setUrval;
  const setUrval = useCallback(
    (newUrval: IUIUrval | null) => {
      if (newUrval) {
        setUrvalProp(newUrval);
      }
    },
    [setUrvalProp]
  );

  const {
    localState: localUrval,
    localSetter: localSetUrval,
    cancel,
    commit,
  } = useCommit<IUIUrval | null>(
    props.urval,
    setUrval,
    handleCommit,
    props.propagateSetUrval
  );
  const smallScreen = useIsBreakpoint('xs');
  const mediumScreen = useIsBreakpoint('md');
  const heights = useMemo(
    () => getUrvalPageHeights(localUrval?.pages, smallScreen, mediumScreen),
    [localUrval?.pages, mediumScreen, smallScreen]
  );
  return (
    <ModelContextConsumer>
      {({ avenyCollectionMap }) => (
        <DefaultUrval
          collectionName={props.collectionName}
          urval={localUrval}
          kodplanUrval={props.kodplanUrval}
          rutinID={props.routineID}
          usageCode={props.usageCode}
        >
          {({ loading, pickedSavedUrval, error }) => {
            return (
              <UrvalContextProvider
                value={{
                  standardGrundUrval:
                    localUrval && localUrval.savedUrvalProps
                      ? localUrval.savedUrvalProps.synlighet ===
                          TEDSelectionVisibility.dbstandard ||
                        localUrval.savedUrvalProps.synlighet ===
                          TEDSelectionVisibility.standard
                      : false,
                }}
              >
                <UrvalActions
                  urval={localUrval}
                  setUrval={localSetUrval}
                  collectionName={props.collectionName}
                >
                  {({ addNewPage, removePage, setPage }) => (
                    <UrvalDialog
                      {...props}
                      loading={loading}
                      error={error}
                      pickedSavedUrval={pickedSavedUrval}
                      urval={localUrval}
                      setUrval={localSetUrval}
                      setPage={setPage}
                      addNewPage={addNewPage}
                      removePage={removePage}
                      cancel={cancel}
                      commit={commit}
                      avenyCollectionMap={avenyCollectionMap}
                      height={Math.max(...heights)}
                      isSmallScreen={smallScreen}
                      disableVirtualTreeSelect={props.disableVirtualTreeSelect}
                      setSelectionOrder={setSelectionOrder}
                    />
                  )}
                </UrvalActions>
              </UrvalContextProvider>
            );
          }}
        </DefaultUrval>
      )}
    </ModelContextConsumer>
  );
};

export default EnhancedUrvalDialog;
